import type { BaseQueryFn, QueryActionCreatorResult, QueryDefinition } from '@reduxjs/toolkit/query';
import Multiselect from '@rio-cloud/rio-uikit/Multiselect';
import Notification from '@rio-cloud/rio-uikit/Notification';
import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import FormDialogFooter from 'components/forms/FormDialogFooter';
import { SystemAdminDialogTypes } from 'helper/types';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateSystemFleetMutation } from 'services/systemAdmin/fleets';
import { useLazyListSystemTeamsQuery } from 'services/systemAdmin/teams';
import {
    clearActiveElements,
    getSelectedFleet,
    setActiveSidebar,
    setSystemAdminOpenDialog,
} from 'store/slices/settingsPageSlice';
import {
    addSelectedTeamsToFleetForm,
    clearFleetFormData,
    clearSelectedTeams,
    clearSelectedVehicles,
    getNameFromFleetForm,
    getSelectedTeamsFromFleetForm,
    getSelectedVehiclesFromFleetForm,
    setFormFleetName,
} from 'store/slices/systemFleet';

import EditFleetInfoTooltip from './EditFleetInfoTooltip';
import InputText from './InputText';
import SelectVehiclesTable from './SelectVehiclesTable';
import type { SystemFleetFormData } from './types';

import type { SystemFleet } from 'API';

import './_styles.scss';

interface Props {
    systemTeams?: SelectOption[];
    isLoadingSystemTeams?: boolean;
    isFetchingFleets?: boolean;
    refetch(): QueryActionCreatorResult<
        QueryDefinition<string, BaseQueryFn<any, unknown, unknown, {}, {}>, never, SystemFleet[], 'systemFleets'>
    >;
}

const SystemFleetEditForm = ({ systemTeams, isLoadingSystemTeams, isFetchingFleets, refetch }: Props) => {
    const inputTeamsId = 'edit-fleet-input-teams';
    const dispatch = useDispatch();
    const selectedFleet = useSelector(getSelectedFleet);
    const nameFromFleetForm = useSelector(getNameFromFleetForm);
    const selectedTeamsFromFleetForm = useSelector(getSelectedTeamsFromFleetForm);
    const selectedVehiclesFromFleetForm = useSelector(getSelectedVehiclesFromFleetForm);

    const [updateFleet, { isLoading: isSubmittingChanges }] = useUpdateSystemFleetMutation({
        fixedCacheKey: 'updateSystemFleet',
    });

    const [fetchSystemTeams] = useLazyListSystemTeamsQuery();

    const closeDialogs = () => {
        dispatch(setSystemAdminOpenDialog(undefined));
    };

    const openCancelEditingDialog = () => {
        dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.EXIT_CONFIRMATION));
    };

    const closeFlow = () => {
        dispatch(setActiveSidebar());
        dispatch(clearActiveElements());
        fetchSystemTeams('ListSystemTeams');
        closeDialogs();
        dispatch(clearFleetFormData());
        Notification.success(<div />, {
            title: `${nameFromFleetForm} fleet changes were saved`,
            timeout: 10_000,
            closeButton: true,
            hideProgressBar: false,
        });
        dispatch(setFormFleetName(undefined));
        dispatch(clearSelectedVehicles(undefined));
        dispatch(clearSelectedTeams(undefined));
    };

    const refreshTable = async () => {
        await refetch()
            .unwrap()
            .then(() => closeFlow());
    };

    const onSubmit = async (formData: SystemFleetFormData) => {
        try {
            if (!selectedFleet?.id) {
                throw new Error('No fleet id selected');
            }

            await updateFleet({
                ...formData,
                id: selectedFleet.id,
                teams: selectedTeamsFromFleetForm ?? [],
                vehicles: selectedVehiclesFromFleetForm ?? [],
            })
                .unwrap()
                .then(() => refreshTable());
        } catch (error) {
            refetch();
            console.error(error);
            dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.ERROR_DIALOG));
        }
    };

    const defaultValues = useMemo(
        () => ({
            name: nameFromFleetForm,
        }),
        [nameFromFleetForm]
    );

    const [isFormValid, setIsValidForm] = useState(true);

    const { register, formState, getValues, handleSubmit, setValue, setError, clearErrors } =
        useForm<SystemFleetFormData>({
            defaultValues,
        });

    const checkValues = () => {
        if (!getValues(['name'])[0].length) {
            setIsValidForm(false);
            setError('name', {});
        } else {
            clearErrors();
            setIsValidForm(true);
        }
    };

    const editFleetName = (name: string) => {
        setValue('name', name);
        dispatch(setFormFleetName(name));
    };

    const selectTeams = (teams: string[]) => {
        dispatch(addSelectedTeamsToFleetForm(teams));
    };

    return (
        <form className='SystemFleetForm' onChange={checkValues} onSubmit={handleSubmit(onSubmit)}>
            <div className='SystemFleetForm__section'>
                <p className='SystemFleetForm__headline'>Operational</p>
                <div className='SystemFleetForm__line SystemFleetForm__line--largeSpacing SystemFleetForm__line--grid'>
                    <InputText
                        label='Name'
                        name='name'
                        defaultValue={nameFromFleetForm}
                        error={formState.errors.name}
                        errorMessage='Mandatory field'
                        register={register}
                        onChange={e => editFleetName(e.currentTarget.value)}
                        required
                    />
                    <div className='SystemFleetForm__formInput'>
                        <label className='SystemFleetForm__inputLabel' htmlFor={inputTeamsId}>
                            Team(s)
                        </label>
                        <Multiselect
                            id={inputTeamsId}
                            className='SystemFleetForm__multiselect'
                            placeholder='Please select'
                            noItemMessage='No teams available.'
                            options={systemTeams}
                            disabled={!systemTeams}
                            value={selectedTeamsFromFleetForm}
                            onChange={e => selectTeams(e)}
                            isLoading={isLoadingSystemTeams}
                            useFilter
                        />
                    </div>
                </div>
            </div>
            <div className='SystemFleetForm__section'>
                <p className='SystemFleetForm__headline'>Vehicles</p>
                <p className='SystemFleetForm__message'>
                    These vehicles are not assigned to any fleet yet
                    <EditFleetInfoTooltip />
                </p>
                <div className='SystemFleetForm__line'>
                    <SelectVehiclesTable />
                </div>
            </div>
            <div className='SystemFleetEditForm__footer'>
                <FormDialogFooter
                    onConfirmLabel='Save changes'
                    onCancel={openCancelEditingDialog}
                    isValid={isFormValid}
                    isLoading={isSubmittingChanges || isFetchingFleets}
                />
            </div>
        </form>
    );
};

export default SystemFleetEditForm;
